import React, { useState, useEffect } from 'react';
import {
  Mail,
  Plus,
  Search,
  Filter,
  Eye,
  Send,
  Trash2,
  RefreshCw,
  Users,
  CheckCircle,
  XCircle,
  Clock,
  FileText,
  AlertTriangle
} from 'lucide-react';
import { AdminApis } from '../../apis/adminApi';

interface EmailCampaign {
  id: number;
  title: string;
  subject: string;
  status: 'draft' | 'sending' | 'sent' | 'failed';
  total_recipients: number;
  sent_count: number;
  failed_count: number;
  success_rate: number;
  created_at: string;
  sent_at: string | null;
  creator: {
    first_name: string;
    last_name: string;
  };
}

const EmailCampaigns: React.FC = () => {
  const [campaigns, setCampaigns] = useState<EmailCampaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [actionLoading, setActionLoading] = useState<{ [key: number]: 'sending' | 'deleting' | null }>({});
  const [notifications, setNotifications] = useState<{ id: string, message: string, type: 'success' | 'error' }[]>([]);

  const showNotification = (message: string, type: 'success' | 'error') => {
    const id = Date.now().toString();
    setNotifications(prev => [...prev, { id, message, type }]);
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 5000);
  };

  const fetchCampaigns = async () => {
    setLoading(true);
    try {
      const query = {
        search: searchTerm,
        status: statusFilter,
        per_page: 10
      };

      const response: any = await AdminApis.getAllEmailCampaigns(currentPage, query);

      if (response.data.status === 'success') {
        setCampaigns(response.data.data.data);
        setTotalPages(response.data.data.last_page);
      }
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      showNotification('Failed to fetch campaigns', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCampaigns();
  }, [currentPage, searchTerm, statusFilter]);

  // Auto-refresh when there are campaigns with 'sending' status
  useEffect(() => {
    const hasSendingCampaigns = campaigns.some(campaign => campaign.status === 'sending');

    if (hasSendingCampaigns) {
      const interval = setInterval(() => {
        console.log('Auto-refreshing due to sending campaigns...');
        fetchCampaigns();
      }, 10000); // Refresh every 10 seconds

      return () => clearInterval(interval);
    }
  }, [campaigns]);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'sent':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'sending':
        return <Clock className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return <FileText className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800';
      case 'sending':
        return 'bg-blue-100 text-blue-800 animate-pulse';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;

    setActionLoading(prev => ({ ...prev, [id]: 'deleting' }));

    try {
      const response: any = await AdminApis.deleteEmailCampaign(id);

      if (response.data.status === 'success') {
        showNotification('Campaign deleted successfully', 'success');
        fetchCampaigns();
      } else {
        showNotification(response.data.message || 'Failed to delete campaign', 'error');
      }
    } catch (error: any) {
      console.error('Error deleting campaign:', error);
      const errorMessage = error.response?.data?.message || 'Failed to delete campaign';
      showNotification(errorMessage, 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const handleSend = async (id: number) => {
    if (!window.confirm('Are you sure you want to send this campaign?')) return;

    setActionLoading(prev => ({ ...prev, [id]: 'sending' }));

    try {
      const response: any = await AdminApis.sendEmailCampaign(id);

      if (response.data.status === 'success') {
        showNotification('Campaign is being sent successfully', 'success');
        fetchCampaigns();
      } else {
        showNotification(response.data.message || 'Failed to send campaign', 'error');
      }
    } catch (error: any) {
      console.error('Error sending campaign:', error);
      const errorMessage = error.response?.data?.message || 'Failed to send campaign';
      showNotification(errorMessage, 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [id]: null }));
    }
  };

  const getCampaignStatusMessage = (campaign: EmailCampaign) => {
    switch (campaign.status) {
      case 'sending':
        return `Sending emails... (${campaign.sent_count}/${campaign.total_recipients} sent)`;
      case 'sent':
        return `Completed - ${campaign.sent_count} sent, ${campaign.failed_count} failed`;
      case 'failed':
        return 'Failed to send';
      default:
        return 'Draft';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 text-gray-400 animate-spin mx-auto" />
          <p className="mt-2 text-gray-500">Loading campaigns...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Notifications */}
      <div className="fixed top-4 right-4 z-50 space-y-2">
        {notifications.map(notification => (
          <div
            key={notification.id}
            className={`px-4 py-3 rounded-lg shadow-lg border-l-4 max-w-sm ${notification.type === 'success'
                ? 'bg-green-50 border-green-500 text-green-800'
                : 'bg-red-50 border-red-500 text-red-800'
              }`}
          >
            <div className="flex items-center gap-2">
              {notification.type === 'success' ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertTriangle className="h-4 w-4" />
              )}
              <span className="text-sm">{notification.message}</span>
            </div>
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Campaigns</h1>
          <p className="text-gray-600">Manage and send email campaigns to users</p>
          {campaigns.some(c => c.status === 'sending') && (
            <div className="flex items-center gap-2 mt-2 text-blue-600">
              <Clock className="h-4 w-4 animate-spin" />
              <span className="text-sm">Campaigns are being sent - auto-refreshing every 10 seconds</span>
            </div>
          )}
        </div>
        <button
          onClick={() => window.location.href = '/admin/email-campaigns/create'}
          className="flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Campaign
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search campaigns..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-gray-400" />
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="sending">Sending</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <button
            onClick={fetchCampaigns}
            className="flex items-center gap-2 px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </div>

      {/* Campaigns List */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        {campaigns.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Campaign
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Recipients
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Progress
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {campaigns.map((campaign) => (
                  <tr key={campaign.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <div>
                        <div className="font-medium text-gray-900">{campaign.title}</div>
                        <div className="text-sm text-gray-500">{campaign.subject}</div>
                        <div className="text-xs text-gray-400">
                          by {campaign.creator.first_name} {campaign.creator.last_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex items-center gap-2">
                          {getStatusIcon(campaign.status)}
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(campaign.status)}`}>
                            {campaign.status.charAt(0).toUpperCase() + campaign.status.slice(1)}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {getCampaignStatusMessage(campaign)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-1">
                        <Users className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-900">{campaign.total_recipients}</span>
                      </div>
                      {(campaign.status === 'sent' || campaign.status === 'sending') && (
                        <div className="text-xs text-gray-500 mt-1">
                          Sent: {campaign.sent_count} | Failed: {campaign.failed_count}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {campaign.status === 'sending' ? (
                        <div className="space-y-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all duration-500 animate-pulse"
                              style={{
                                width: `${campaign.total_recipients > 0 ? (campaign.sent_count / campaign.total_recipients) * 100 : 0}%`
                              }}
                            />
                          </div>
                          <div className="text-xs text-blue-600 font-medium">
                            {campaign.total_recipients > 0
                              ? `${Math.round((campaign.sent_count / campaign.total_recipients) * 100)}%`
                              : '0%'
                            } sent
                          </div>
                        </div>
                      ) : campaign.status === 'sent' ? (
                        <div className="space-y-2">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-500 h-2 rounded-full"
                              style={{ width: `${campaign.success_rate}%` }}
                            />
                          </div>
                          <div className="text-xs text-green-600 font-medium">
                            {campaign.success_rate}% success
                          </div>
                        </div>
                      ) : (
                        <span className="text-sm text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {new Date(campaign.created_at).toLocaleDateString()}
                      </div>
                      <div className="text-xs text-gray-500">
                        {new Date(campaign.created_at).toLocaleTimeString()}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => window.location.href = `/admin/email-campaigns/${campaign.id}`}
                          className="p-1 text-gray-400 hover:text-blue-500 transition-colors"
                          title="View Details"
                        >
                          <Eye className="h-4 w-4" />
                        </button>

                        {campaign.status === 'draft' && (
                          <button
                            onClick={() => handleSend(campaign.id)}
                            disabled={actionLoading[campaign.id] === 'sending'}
                            className="p-1 text-gray-400 hover:text-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Send Campaign"
                          >
                            {actionLoading[campaign.id] === 'sending' ? (
                              <RefreshCw className="h-4 w-4 animate-spin text-green-500" />
                            ) : (
                              <Send className="h-4 w-4" />
                            )}
                          </button>
                        )}

                        {campaign.status !== 'sending' && (
                          <button
                            onClick={() => handleDelete(campaign.id)}
                            disabled={actionLoading[campaign.id] === 'deleting'}
                            className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            title="Delete Campaign"
                          >
                            {actionLoading[campaign.id] === 'deleting' ? (
                              <RefreshCw className="h-4 w-4 animate-spin text-red-500" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-center py-12">
            <Mail className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No campaigns found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by creating your first email campaign.
            </p>
            <div className="mt-6">
              <button
                onClick={() => window.location.href = '/admin/email-campaigns/create'}
                className="inline-flex items-center gap-2 bg-orange-500 text-white px-4 py-2 rounded-lg hover:bg-orange-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create Campaign
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center gap-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Previous
          </button>
          <span className="px-4 py-2 text-sm text-gray-600">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition-colors"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default EmailCampaigns;